import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFetchPartner, useFetchPartnerServiceCreation } from '../../hooks/mssp-api-hooks';
import ServiceCreationProduct from './ServiceCreationProduct';
import { ServiceCreation } from '../../types/msp';
import { HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import ServiceCreationActivitiesTable from './ServiceCreationActivitiesTable';

const PartnerServiceCreation: React.FC<{ setRefreshTrigger: (value: (prev: number) => number) => void }> = ({ setRefreshTrigger }) => {
  const { id } = useParams();
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [serviceCreations, setServiceCreations] = useState<ServiceCreation[]>([]);
  const { data: partner, isLoading: isLoadingPartner } = useFetchPartner(Number(id));

  // Fetch service creations with the current refresh trigger
  const { data: fetchedServiceCreations, isLoading: isLoadingServiceCreations } = useFetchPartnerServiceCreation(Number(id));

  // Update `serviceCreations` whenever new data is fetched
  useEffect(() => {
    if (fetchedServiceCreations) {
      setServiceCreations(fetchedServiceCreations);
    }
  }, [fetchedServiceCreations]);

  // Automatically set the first product as the selected product if not already selected
  useEffect(() => {
    if (serviceCreations.length > 0 && serviceCreations[0]?.product && !selectedProductId) {
      setSelectedProductId(serviceCreations[0].product.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCreations]);

  // Handle product selection change
  const onProductSelectChange = (event) => {
    setSelectedProductId(Number(event.target.value));
    setRefreshTrigger((prev) => prev + 1);
  };

  // Handle adding a new service creation product
  const onNewServiceProduct = (prodId: number) => {
    // Update selected product ID to the newly added product
    setSelectedProductId(prodId);

    // Trigger a refresh to reload the service creations
    setRefreshTrigger((prev) => prev + 1);
  };

  if (isLoadingPartner || isLoadingServiceCreations) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {/* Dropdown to select a product */}
        <HbrSelect
          placeholder="Select Product"
          name="basic"
          onHbr-change={onProductSelectChange}
          className="hbr-css__form-section-sm"
          value={selectedProductId?.toString() || ''} // Set the current value of the dropdown
        >
          {(serviceCreations ?? []).filter((sc) => sc.product).map((sc) => (
            <HbrOption key={sc.product.id} value={sc.product.id.toString()}>
              {sc.product.name}
            </HbrOption>
          ))}
        </HbrSelect>

        {/* Button to add a new service creation product */}
        <ServiceCreationProduct selectedProducts={(serviceCreations ?? []).filter((sc) => sc.product).map((sc) => sc.product.id)} onProductSelect={onNewServiceProduct} />
      </div>
      <br />

      {/* Activities Table */}
      {partner && selectedProductId && (
        <ServiceCreationActivitiesTable
          key={selectedProductId} // Force re-render when the selected product changes
          partnerId={partner.id}
          productId={selectedProductId}
        />
      )}
    </div>
  );
};

export default PartnerServiceCreation;